import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import { setAlarm, setNewCancelToken } from '../../../redux/actions/general';
import { cancelTokenSelector, hashSelector } from '../../../redux/selectors/selectors';

import { getHttpHeader } from '../../../util/FetchTools';
import IpTable from './ip/IpTable';
import JwtTable from './jwt/JwtTable';

import { PropTypes } from 'prop-types';
import momentPropTypes from 'react-moment-proptypes';

import { CenteredMsgShowing, StandardContainer } from '../../../styledComponents/styledComponents';
import ErrorDisplay from './ErrorDisplay';
import { GUARDIAN_IP, GUARDIAN_TOKEN, IP_BLOCKLIST } from '../../../constants/general';
import useBlockListFetcher from '../../../hooks/useBlockListFetcher';

import { useQuery } from '@tanstack/react-query';
import { getUnifiedErrorObject } from '../../../util/ErrorUnifier';

const PathVariableMap = {
   [GUARDIAN_IP]: GUARDIAN_IP,
   [GUARDIAN_TOKEN]: 'token/playback',
};

function TableView({
   start,
   end,
   misuseFilter,
   hasRequestStarted,
   handleRequestCompleted,
   hasRequestCancelled,
   setHasRequestCancelled,
   triggerDataFetchManually,
   setLoadingState,
   concurrencyIsDesired,
   aggTerm,
}) {
   const { blockedMap, fetchBlockList: triggerBlockListRefreshFn } = useBlockListFetcher({ type: IP_BLOCKLIST });

   const dispatch = useDispatch();

   const [result, setResult] = useState([]);
   const [tableData, setTableData] = useState([]);

   const [tableType, setTableType] = useState(aggTerm === GUARDIAN_IP ? GUARDIAN_IP : GUARDIAN_TOKEN);

   const hash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   const httpHeader = useMemo(() => getHttpHeader(hash), [hash]);

   const url = useMemo(
      () =>
         `/v2/usage/${
            PathVariableMap[aggTerm]
         }/details?from=${start.format()}&to=${end.format()}&concurrency=${concurrencyIsDesired}`,
      [concurrencyIsDesired, start, end, aggTerm],
   );

   const requestBody = useMemo(() => buildRequestBody(misuseFilter), [misuseFilter]);

   // console.log('++++++hasRequestStarted => ', hasRequestStarted);

   const { error, isLoading, data } = useQuery({
      enabled: hasRequestStarted,
      queryKey: [url, hash, cancelToken, requestBody],
      queryFn: () => {
         return axios
            .post(url, requestBody, {
               headers: httpHeader,
               cancelToken: cancelToken.token,
            })
            .then((res) => {
               return res.data;
            });
      },
      onSuccess: (res) => {
         setResult(res.data);
      },
      onError: (error) => {
         if (error instanceof axios.Cancel) {
            setResult([]);
            return;
         }
         setResult([]);
         dispatch(setAlarm('danger', error));
      },
      onSettled: () => {
         // console.log('onSettled => ');
         handleRequestCompleted();
         setLoadingState(false);
      },
      retry: false,
      refetchOnWindowFocus: false,
   });
   useEffect(() => {
      triggerBlockListRefreshFn();
   }, [url, httpHeader, cancelToken, triggerBlockListRefreshFn]);

   useEffect(() => {
      setTableData(aggTerm === GUARDIAN_IP ? addBlockState(result, blockedMap) : result);
      setTableType(aggTerm === GUARDIAN_IP ? GUARDIAN_IP : GUARDIAN_TOKEN);
   }, [result, blockedMap, aggTerm]);

   const errorMessage = getErrorMessage(error);

   const isPristine = errorMessage === undefined && data === undefined;
   const noData = result.length === 0;
   const errorOrNoDataOrPristine = errorMessage || noData || isPristine || isLoading;

   return (
      <>
         <StandardContainer $borderRadiusBLpx={7} $borderRadiusBRpx={7}>
            {errorOrNoDataOrPristine || hasRequestCancelled ? (
               <CenteredMsgShowing
                  height={200}
                  marginTopPx={0}
                  $fontColor={'rgb(150, 150, 150)'}
                  $bgColor={'rgb(255,255,255)'}
                  $fontSizePx={16}
               >
                  {hasRequestCancelled ? (
                     <CenteredMsgShowing
                        flexDirection='column'
                        $fontColor={'rgb(150, 150, 150)'}
                        $bgColor={'rgb(255,255,255)'}
                        $fontSizePx={16}
                     >
                        <>{'Request cancelled'}</>
                        <button
                           onClick={() => {
                              triggerDataFetchManually();
                           }}
                           type='button'
                           className='btn btn-primary mt-4'
                        >
                           Retry
                        </button>
                     </CenteredMsgShowing>
                  ) : isPristine ? (
                     <>{'Please start a request'}</>
                  ) : errorMessage ? (
                     <CenteredMsgShowing
                        flexDirection='column'
                        $fontColor={'rgb(150, 150, 150)'}
                        $bgColor={'rgb(255,255,255)'}
                        $fontSizePx={16}
                     >
                        <ErrorDisplay error={errorMessage} msgColor={'rgb(80,80,80)'} />
                        <button
                           onClick={() => {
                              triggerDataFetchManually();
                           }}
                           type='button'
                           className='btn btn-primary mt-4'
                        >
                           Retry
                        </button>
                     </CenteredMsgShowing>
                  ) : data && data.data && data.data.length === 0 ? (
                     <>{'No data available'}</>
                  ) : (
                     <CenteredMsgShowing
                        flexDirection='column'
                        $fontColor={'rgb(150, 150, 150)'}
                        $bgColor={'rgb(255,255,255)'}
                        $fontSizePx={16}
                     >
                        <div>{'Fetching data ...'}</div>
                        <button
                           onClick={() => {
                              cancelToken.cancel('Operation canceled by the user.');
                              dispatch(setNewCancelToken(axios.CancelToken.source()));
                              setHasRequestCancelled(true);
                           }}
                           type='button'
                           className='btn btn-primary mt-4'
                        >
                           Stop Fetching
                        </button>
                     </CenteredMsgShowing>
                  )}
               </CenteredMsgShowing>
            ) : (
               <>
                  {tableType === GUARDIAN_IP ? (
                     <IpTable
                        data={!hasRequestStarted && hasTableDataWith('ip', tableData) ? tableData : []}
                        concurrencyIsDesired={concurrencyIsDesired}
                        url={url}
                        httpHeader={httpHeader}
                        cancelToken={cancelToken}
                        requestBody={requestBody}
                     />
                  ) : (
                     <JwtTable
                        data={!hasRequestStarted && hasTableDataWith('playbackToken', tableData) ? tableData : []}
                        concurrencyIsDesired={concurrencyIsDesired}
                        url={url}
                        httpHeader={httpHeader}
                        cancelToken={cancelToken}
                        requestBody={requestBody}
                     />
                  )}
               </>
            )}
         </StandardContainer>
      </>
   );
}

function getErrorMessage(error) {
   if (error instanceof axios.Cancel) {
      return null;
   }
   return error ? getUnifiedErrorObject(error) : null;
}

function hasTableDataWith(prop, table) {
   return table.length > 0 && Object.hasOwn(table[0], prop);
}

export function buildRequestBody(misuseFilter) {
   const keys = ['streams', 'ips', 'jtis', 'referrers', 'countries', 'providers', 'users', 'tags'];
   const body = {};
   keys.forEach((key) => {
      body[key] = (misuseFilter?.[key] ?? []).map((item) => item.value);
   });
   return body;
}

TableView.propTypes = {
   misuseFilter: PropTypes.object,
   start: momentPropTypes.momentObj,
   end: momentPropTypes.momentObj,
   hasRequestStarted: PropTypes.bool,
   handleRequestCompleted: PropTypes.func,
   hasRequestCancelled: PropTypes.bool,
   setHasRequestCancelled: PropTypes.func,
   triggerDataFetchManually: PropTypes.func,
   setLoadingState: PropTypes.func,
   concurrencyIsDesired: PropTypes.bool,
   aggTerm: PropTypes.string,
};

function addBlockState(resultList, blockedMap) {
   return blockedMap !== undefined
      ? resultList.map((row) =>
           blockedMap[row.ip] ? { ...row, blocked: true, action: row.ip } : { ...row, blocked: false, action: row.ip },
        )
      : resultList.map((row) => ({ ...row, blocked: 'N/A', action: '' }));
}

export default TableView;
